import { AfterViewInit, Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DocumentsService } from '../../../services/documents.service';
import { catchError, filter, NEVER, startWith, switchMap, tap } from 'rxjs';
import { ShipmentDocument } from '../../../models/documents';
import { DatePipe, NgOptimizedImage } from '@angular/common';
import { FileIconPipe } from '../../../../../pipes/file-icon.pipe';
import { DocumentListSkeletonComponent } from '../document-list-skeleton/document-list-skeleton.component';
import { HttpErrorResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { MatSnackBar } from '@angular/material/snack-bar';
import { openConfirmationDialog } from '../../../../confirmation-popup/confirmation-popup';
import { MatDialog } from '@angular/material/dialog';
import { dateTimeFormat, userTimezone } from '../../../../../models/date-formats';
import { MatCheckbox } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-document-list',
  standalone: true,
  templateUrl: './document-list.component.html',
  imports: [
    DatePipe,
    FileIconPipe,
    NgOptimizedImage,
    DocumentListSkeletonComponent,
    MatCheckbox,
    MatButton,
    MatIcon,
    MatProgressSpinner
  ],
  styleUrl: './document-list.component.scss'
})
export class DocumentListComponent implements OnChanges, AfterViewInit {
  @Input() shipmentNumber?: string | null = null;
  @Input() isArchived = false;

  documents: ShipmentDocument[] = [];
  isLoading = true;
  selection = new SelectionModel<string>(true, []);
  downloading = false;
  selectedDocumentIds: ShipmentDocument['id'][] = [];
  readonly dateTimeFormat = dateTimeFormat;

  readonly userTimezone = userTimezone;
  private documentsService = inject(DocumentsService);
  private matSnackBar = inject(MatSnackBar);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shipmentNumber'] && changes['shipmentNumber'].currentValue) {
      this.getDocumentList();
    }
  }

  ngAfterViewInit() {
    this.handleSelectionChange();
  }

  download({ fileName, id, fileType }: ShipmentDocument) {
    const source$ = this.isArchived
      ? this.documentsService.getArchivedDocument(this.shipmentNumber!, id)
      : this.documentsService.getDocument(this.shipmentNumber!, id);
    source$
      .pipe(
        tap(file => saveAs(file, `${fileName}.${fileType}`)),
        catchError(error => {
          this.matSnackBar.open('Could not download the file, please try again later.', 'x');
          console.error(error);
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  downloadMultiple() {
    if (!this.downloading) {
      this.downloading = true;
      let selectedIds = this.selectedDocumentIds;
      if (this.selectedDocumentIds.length === 0) {
        selectedIds = this.documents.map(({ id }) => id);
      }
      const source$ = this.isArchived
        ? this.documentsService.getManyArchivedDocuments(this.shipmentNumber!, selectedIds)
        : this.documentsService.getManyDocuments(this.shipmentNumber!, selectedIds);
      source$
        .pipe(
          tap(file => {
            this.downloading = false;
            saveAs(file, `shipment-documents-${this.shipmentNumber!}`);
          }),
          catchError(error => {
            console.error(error);
            this.downloading = false;
            this.matSnackBar.open('Could not download documents. Please try again later.', 'x');

            return NEVER;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  delete(id: string) {
    openConfirmationDialog(this.matDialog, {
      title: 'Delete document',
      message: 'Are you sure you want to delete this document?'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        switchMap(() =>
          this.documentsService.deleteDocument(this.shipmentNumber!, id).pipe(
            tap(() => {
              this.documentsService.refreshDocuments();
              this.matSnackBar.open('The file was deleted successfully.', 'x', { panelClass: 'snackbar-green' });
            }),
            catchError(error => {
              console.error(error);
              this.matSnackBar.open('Could not delete the file, please try again later.', 'x');
              return NEVER;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  handleSelectionChange() {
    this.selection.changed
      .pipe(
        tap(() => (this.selectedDocumentIds = this.selection.selected)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.documents.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.documents.map(({ id }) => id));
  }

  private getDocumentList() {
    this.documentsService
      .onRefreshDocumentList()
      .pipe(
        startWith(null),
        tap(() => (this.isLoading = true)),
        switchMap(() =>
          this.isArchived
            ? this.documentsService.getAllArchivedDocuments(this.shipmentNumber!)
            : this.documentsService.getAllDocuments(this.shipmentNumber!)
        ),
        tap(docs => {
          this.documents = docs;
          this.isLoading = false;
        }),
        catchError((error: HttpErrorResponse) => {
          this.isLoading = false;
          console.error(error);

          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
